import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { TruncateWithTooltip } from "shared/components/TruncateWithTooltip";
import type { LeadsBySourceItem } from "api/crm-analytics";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers/media-query/use-media";
import { getNiceAxis, getTicks } from "../utils";
import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { FALLBACK_COLOR } from "./modules/constants/colors";

export const SourceBarChartCard = ({
  title,
  subtitle,
  data,
  isLoading
}: {
  title: string;
  subtitle: string;
  data: LeadsBySourceItem[] | undefined;
  isLoading: boolean;
}) => {
  const { isMobile } = useMedia();
  const visibleData = data ?? [];

  const hasData = visibleData.length > 0;
  const showEmptyState = !isLoading && !hasData;
  const showChart = !isLoading && hasData;
  const maxCount = hasData ? Math.max(...visibleData.map((d) => d.count)) : 0;
  const { axisMax, tickCount } = hasData
    ? getNiceAxis(maxCount)
    : { axisMax: 0, tickCount: 0 };
  const ticks = hasData ? getTicks(axisMax, tickCount) : [];

  // h-full because both callers sit in the narrow column of a stretch row
  // (beside Leads over time / Application pipeline): the grid stretches the
  // wrapper, and without it the card floats short of that. No effect where the
  // two columns already come out the same height.
  return (
    <Card className="p-6 w-full h-full">
      <div className="space-y-1">
        <p className="text-base font-medium text-content-700">{title}</p>
        <p className="text-sm font-medium text-content-300">{subtitle}</p>
      </div>

      <Show if={isLoading}>
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={showEmptyState}>
        <AnalyticsEmptyState description="There are no data to show for the selected filter" />
      </Show>

      <Show if={showChart}>
        <div className={cx("mt-6", isMobile ? "overflow-x-auto" : "")}>
          <div className={cx("flex gap-4", isMobile ? "min-w-[400px]" : "")}>
            <div className="shrink-0 w-full max-w-[104px] space-y-2 mt-2">
              {visibleData.map((item) => (
                <div key={item.source_id} className="h-6 flex items-center">
                  <TruncateWithTooltip
                    content={item.source_name}
                    className="text-xs font-medium text-content-1000 cursor-pointer"
                  />
                </div>
              ))}
            </div>

            <div
              className={cx("relative flex-1", isMobile ? "max-w-[200px]" : "")}
            >
              <div className="relative space-y-2">
                <div className="absolute inset-0 pointer-events-none">
                  {ticks.map((tick) => (
                    <div
                      key={tick}
                      className="absolute top-0 h-full border-l border-dashed border-alpha-100"
                      style={{ left: `${(tick / axisMax) * 100}%` }}
                    />
                  ))}
                </div>
                {visibleData.map((item) => (
                  <div key={item.source_id} className="relative h-6">
                    <div
                      className="absolute inset-y-0 left-0 rounded-lg"
                      style={{
                        width: `${(item.count / axisMax) * 100}%`,
                        minWidth: "4px",
                        backgroundColor: item.color ?? FALLBACK_COLOR
                      }}
                    />
                    <span
                      className="absolute top-1/2 -translate-y-1/2 text-xs font-bold text-content-1000"
                      style={{
                        left: `calc(${(item.count / axisMax) * 100}% + 8px)`
                      }}
                    >
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>

              <div className="relative h-5 mt-2">
                {ticks.map((tick) => (
                  <span
                    key={tick}
                    className="absolute text-xs font-medium text-content-300"
                    style={{
                      left: `${(tick / axisMax) * 100}%`,
                      transform: "translateX(-50%)"
                    }}
                  >
                    {tick}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Show>
    </Card>
  );
};
